import { useEffect, useCallback, useRef, useState } from 'react';
import analyticsManager, { 
  trackEvent as trackAnalyticsEvent,
  identifyUser as identifyAnalyticsUser,
  setUserProperties as setAnalyticsUserProperties,
  trackPageView as trackAnalyticsPageView,
  trackTiming as trackAnalyticsTiming,
  createUserGroup as createAnalyticsUserGroup,
  setUserConsent as setAnalyticsConsent
} from '../utils/analytics';

/**
 * Custom React hook for analytics tracking
 * Provides easy-to-use analytics functions with React lifecycle management
 */
export const useAnalytics = () => {
  const [isInitialized, setIsInitialized] = useState(analyticsManager.isInitialized);
  const [hasUserConsent, setHasUserConsent] = useState(analyticsManager.userConsent);
  const mountedRef = useRef(true);
  const timingStartRef = useRef({});

  // Initialize analytics on mount
  useEffect(() => {
    if (!analyticsManager.isInitialized) {
      analyticsManager.initializeAnalytics().then(() => {
        if (mountedRef.current) {
          setIsInitialized(true);
        }
      });
    }

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Track custom event
   */
  const trackEvent = useCallback((eventName, properties = {}) => {
    if (!mountedRef.current) return;
    trackAnalyticsEvent(eventName, properties);
  }, []);

  /**
   * Identify user
   */
  const identifyUser = useCallback((userId, properties = {}) => {
    if (!mountedRef.current) return;
    identifyAnalyticsUser(userId, properties);
  }, []);

  /**
   * Set user properties
   */
  const setUserProperties = useCallback((properties) => {
    if (!mountedRef.current) return;
    setAnalyticsUserProperties(properties);
  }, []);

  /**
   * Track page view
   */
  const trackPageView = useCallback((pageName, properties = {}) => {
    if (!mountedRef.current) return;
    trackAnalyticsPageView(pageName, properties);
  }, []);

  /**
   * Start timing measurement
   */
  const startTiming = useCallback((category, variable) => {
    const key = `${category}_${variable}`;
    timingStartRef.current[key] = performance.now();
  }, []);

  /**
   * End timing measurement and track
   */
  const endTiming = useCallback((category, variable, label = '') => {
    const key = `${category}_${variable}`;
    const startTime = timingStartRef.current[key];
    
    if (startTime) {
      const duration = Math.round(performance.now() - startTime);
      trackAnalyticsTiming(category, variable, duration, label);
      delete timingStartRef.current[key];
    }
  }, []);

  /**
   * Track form field interaction
   */
  const trackFieldInteraction = useCallback((fieldName, action, value = null) => {
    trackEvent('field_interaction', {
      field_name: fieldName,
      action: action, // focus, blur, change, validate
      has_value: value !== null && value !== '',
      value_length: value ? value.toString().length : 0
    });
  }, [trackEvent]);

  /**
   * Track button click
   */
  const trackButtonClick = useCallback((buttonName, location = '', metadata = {}) => {
    trackEvent('button_click', {
      button_name: buttonName,
      location: location,
      ...metadata
    });
  }, [trackEvent]);

  /**
   * Track error
   */
  const trackError = useCallback((errorType, errorMessage, location = '') => {
    trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      location: location
    });
  }, [trackEvent]);

  /**
   * Track search
   */
  const trackSearch = useCallback((searchTerm, resultsCount = 0, searchType = 'general') => {
    trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
      search_type: searchType
    });
  }, [trackEvent]);

  /**
   * Track feature usage
   */
  const trackFeatureUsage = useCallback((featureName, action = 'used', metadata = {}) => {
    trackEvent('feature_usage', {
      feature_name: featureName,
      action: action,
      ...metadata
    });
  }, [trackEvent]);

  /**
   * Track conversion
   */
  const trackConversion = useCallback((conversionType, value = null, metadata = {}) => {
    trackEvent('conversion', {
      conversion_type: conversionType,
      conversion_value: value,
      ...metadata
    });
  }, [trackEvent]);

  /**
   * Create B2B group
   */
  const createUserGroup = useCallback((groupType, groupId, properties = {}) => {
    createAnalyticsUserGroup(groupType, groupId, properties);
  }, []);

  /**
   * Set user consent
   */
  const setUserConsent = useCallback((consent) => {
    setAnalyticsConsent(consent);
    setHasUserConsent(consent);
  }, []);

  /**
   * Track engagement time
   */
  const trackEngagementTime = useCallback((pageName, timeSpent) => {
    trackEvent('engagement_time', {
      page_name: pageName,
      time_spent: timeSpent
    });
  }, [trackEvent]);

  return {
    // Core functions
    trackEvent,
    identifyUser,
    setUserProperties,
    trackPageView,
    createUserGroup,
    setUserConsent,
    
    // Convenience functions
    trackFieldInteraction,
    trackButtonClick,
    trackError,
    trackSearch,
    trackFeatureUsage,
    trackConversion,
    trackEngagementTime,
    
    // Timing functions
    startTiming,
    endTiming,
    
    // Status
    isInitialized,
    hasUserConsent
  };
};

/**
 * Hook for tracking page views automatically
 */
export const usePageTracking = (pageName, properties = {}) => {
  const { trackPageView, startTiming, endTiming } = useAnalytics();
  const timeRef = useRef(Date.now());

  useEffect(() => {
    // Track page view
    trackPageView(pageName, properties);
    
    // Start timing for page engagement
    startTiming('page_engagement', pageName);
    
    // Copy the ref value to avoid stale closure issues
    const startTime = timeRef.current;
    
    // Track time spent on page when unmounting
    return () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      endTiming('page_engagement', pageName);
      
      if (timeSpent > 0) {
        trackAnalyticsEvent('page_exit', {
          page_name: pageName,
          time_spent: timeSpent,
          ...properties
        });
      }
    };
  }, [pageName, startTiming, endTiming, trackPageView, properties]);
};

/**
 * Hook for tracking component visibility
 */
export const useVisibilityTracking = (componentName, threshold = 0.5) => {
  const { trackEvent } = useAnalytics();
  const elementRef = useRef(null);
  const hasBeenVisible = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible.current) {
          hasBeenVisible.current = true;
          trackEvent('component_visible', {
            component_name: componentName,
            visibility_threshold: threshold
          });
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [componentName, threshold, trackEvent]);

  return elementRef;
};

/**
 * Hook for tracking form interactions
 */
export const useFormTracking = (formName) => {
  const { trackEvent, startTiming, endTiming } = useAnalytics();
  const fieldInteractions = useRef({});
  const formStartTime = useRef(null);

  const trackFormStart = useCallback(() => {
    if (!formStartTime.current) {
      formStartTime.current = Date.now();
      startTiming('form_completion', formName);
      trackEvent('form_started', {
        form_name: formName
      });
    }
  }, [formName, trackEvent, startTiming]);

  const trackFieldFocus = useCallback((fieldName) => {
    trackFormStart();
    if (!fieldInteractions.current[fieldName]) {
      fieldInteractions.current[fieldName] = { focuses: 0, changes: 0 };
    }
    fieldInteractions.current[fieldName].focuses++;
    
    trackEvent('form_field_focus', {
      form_name: formName,
      field_name: fieldName,
      focus_count: fieldInteractions.current[fieldName].focuses
    });
  }, [formName, trackEvent, trackFormStart]);

  const trackFieldChange = useCallback((fieldName, value) => {
    if (!fieldInteractions.current[fieldName]) {
      fieldInteractions.current[fieldName] = { focuses: 0, changes: 0 };
    }
    fieldInteractions.current[fieldName].changes++;
    
    trackEvent('form_field_change', {
      form_name: formName,
      field_name: fieldName,
      has_value: !!value,
      change_count: fieldInteractions.current[fieldName].changes
    });
  }, [formName, trackEvent]);

  const trackFormSubmit = useCallback((success = true, validationErrors = []) => {
    const timeSpent = formStartTime.current ? 
      Math.round((Date.now() - formStartTime.current) / 1000) : 0;
    
    endTiming('form_completion', formName);
    
    trackEvent('form_submitted', {
      form_name: formName,
      success: success,
      time_spent: timeSpent,
      field_interactions: Object.keys(fieldInteractions.current).length,
      validation_errors: validationErrors.length,
      error_fields: validationErrors
    });

    // Reset tracking
    formStartTime.current = null;
    fieldInteractions.current = {};
  }, [formName, trackEvent, endTiming]);

  const trackFormAbandon = useCallback((reason = 'unknown') => {
    const timeSpent = formStartTime.current ? 
      Math.round((Date.now() - formStartTime.current) / 1000) : 0;
    
    trackEvent('form_abandoned', {
      form_name: formName,
      reason: reason,
      time_spent: timeSpent,
      field_interactions: Object.keys(fieldInteractions.current).length,
      last_field: Object.keys(fieldInteractions.current).pop()
    });

    // Reset tracking
    formStartTime.current = null;
    fieldInteractions.current = {};
  }, [formName, trackEvent]);

  return {
    trackFormStart,
    trackFieldFocus,
    trackFieldChange,
    trackFormSubmit,
    trackFormAbandon
  };
};

export default useAnalytics;